import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Trash2 } from 'lucide-react';
import { internalApi } from '../lib/hubApi';
import { DeleteRecordDialog } from '../components/shared/DeleteRecordDialog';
import { confirmWordFor, deleteRecordNotice, type DeleteOutcome } from '../lib/deleteRecord';
import { HealthGauge } from '../components/ui/HealthGauge';
import { EmptyState } from '../components/ui/EmptyState';
import { AutomationByObjectTab } from '../components/automation/AutomationByObjectTab';
import { AutomationTop10Card } from '../components/automation/AutomationTop10Card';
import { ScanReportsTab } from '../components/reports/ScanReportsTab';
import { ObjectsPage } from './ObjectsPage';
import { UsersPage } from './UsersPage';
import { UserActivityPage } from './UserActivityPage';
import { PermissionsPage } from './PermissionsPage';
import { LicensesPage } from './LicensesPage';
import { AppsPage } from './AppsPage';
import { StoragePage } from './StoragePage';
import { DataHygienePage } from './DataHygiene';
import { TechDebtPage } from './TechDebtPage';
import { FoundationsPage } from './FoundationsPage';
import { EmailDeliverabilityPage } from './EmailDeliverabilityPage';
import { DataCloudPage } from './DataCloudPage';
import type { AuditBundle, HealthScore } from '../types/org.types';
import {
  AppsScanDetail,
  RiskyPermissionMatrix,
  type IntegrationSecurityResult,
} from '../components/scan/IntegrationSecurityPanels';

type Severity = 'high' | 'medium' | 'low' | 'info';
type Finding = { id: string; severity: Severity; category: string; title: string; context?: string };

// The snapshot bundle = the consultant AuditBundle plus the two fields the
// orchestrator stamps on capture.
type SnapshotBundle = AuditBundle & {
  findings: Finding[];
  capturedAt: number;
  integrationsecurity?: IntegrationSecurityResult | null; // v2.6, absent on older scans
  automationDetail?: unknown; // v2.6, absent on older scans; only its presence is read here
};

type Snapshot = {
  leadId: string;
  orgId: string;
  orgName: string;
  orgType: 'production' | 'sandbox';
  capturedAt: number;
  bundle: SnapshotBundle;
};

// Same Section 11 sub-scores the teaser shows, full width here.
const CATEGORIES = [
  { key: 'automation', label: 'Automation', max: 25 },
  { key: 'security', label: 'Security', max: 25 },
  { key: 'dataQuality', label: 'Data quality', max: 20 },
  { key: 'metadata', label: 'Metadata', max: 15 },
  { key: 'users', label: 'Users', max: 15 },
] as const;

const SEV_STYLE: Record<Severity, string> = {
  high: 'bg-sunset/15 text-sunset border-sunset/40',
  medium: 'bg-sunrise/15 text-sunrise border-sunrise/40',
  low: 'bg-adopt/15 text-adopt border-adopt/40',
  info: 'bg-surface-3 text-neutral-400 border-border',
};
const SEV_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2, info: 3 };

type Tab = 'audit' | 'automation' | 'data-model' | 'categories' | 'reports';
const TABS: { key: Tab; label: string }[] = [
  { key: 'audit', label: 'Audit' },
  { key: 'automation', label: 'Automation by object' },
  { key: 'data-model', label: 'Data model' },
  { key: 'categories', label: 'Categories' },
  { key: 'reports', label: 'Reports' },
];

// The stored audit views, in the order the sub-menu lists them.
type Category =
  | 'users'
  | 'activity'
  | 'permissions'
  | 'licenses'
  | 'apps'
  | 'storage'
  | 'data-hygiene'
  | 'tech-debt'
  | 'foundations'
  | 'email'
  | 'data-cloud';

const CATEGORY_MENU: { key: Category; label: string }[] = [
  { key: 'users', label: 'Users' },
  { key: 'activity', label: 'User Activity' },
  { key: 'permissions', label: 'Permissions' },
  { key: 'licenses', label: 'Licenses' },
  { key: 'apps', label: 'Apps' },
  { key: 'storage', label: 'Storage' },
  { key: 'data-hygiene', label: 'Data Hygiene' },
  { key: 'tech-debt', label: 'Tech Debt' },
  { key: 'foundations', label: 'Foundations' },
  { key: 'email', label: 'Email' },
  { key: 'data-cloud', label: 'Data Cloud' },
];

// A scan captured before v2.4 carries no per-object automation and no record
// counts. Say so plainly rather than showing an empty table.
function NotCaptured({ what }: { what: string }) {
  return (
    <EmptyState
      title={`${what} was not captured for this scan.`}
      hint="This prospect connected before the view existed. A new scan captures it."
    />
  );
}

// One consultant audit view per category, rendered from the stored bundle
// through each page's `data` prop, so no /api/org/* call fires here. A category
// the scan could not read is null in the bundle and says so instead.
function CategoryView({ category, bundle }: { category: Category; bundle: SnapshotBundle }) {
  switch (category) {
    case 'users':
      return bundle.users ? (
        <UsersPage data={bundle.users} permissionsData={bundle.permissions} />
      ) : (
        <NotCaptured what="The user audit" />
      );
    case 'activity':
      return bundle.activity ? (
        <UserActivityPage data={bundle.activity} />
      ) : (
        <NotCaptured what="The user activity audit" />
      );
    case 'permissions':
      return (
        <div className="space-y-6">
          {bundle.permissions ? (
            <PermissionsPage data={bundle.permissions} />
          ) : (
            <NotCaptured what="The permissions audit" />
          )}
          <RiskyPermissionMatrix data={bundle.integrationsecurity} />
        </div>
      );
    case 'licenses':
      return bundle.licenses ? (
        <LicensesPage data={bundle.licenses} />
      ) : (
        <NotCaptured what="The licence inventory" />
      );
    case 'apps':
      return (
        <div className="space-y-6">
          {bundle.apps ? <AppsPage data={bundle.apps} /> : <NotCaptured what="The installed apps audit" />}
          <AppsScanDetail
            data={bundle.integrationsecurity}
            apiLimit={bundle.overview?.limits?.DailyApiRequests}
          />
        </div>
      );
    case 'storage':
      return bundle.storage ? (
        <StoragePage data={bundle.storage} />
      ) : (
        <NotCaptured what="The storage audit" />
      );
    case 'data-hygiene':
      return bundle.datahygiene ? (
        <DataHygienePage data={bundle.datahygiene} />
      ) : (
        <NotCaptured what="The data hygiene audit" />
      );
    case 'tech-debt':
      return bundle.techdebt ? (
        <TechDebtPage data={bundle.techdebt} />
      ) : (
        <NotCaptured what="The tech debt audit" />
      );
    case 'foundations':
      return bundle.foundations ? (
        <FoundationsPage data={bundle.foundations} />
      ) : (
        <NotCaptured what="The Foundations audit" />
      );
    case 'email':
      return bundle.email ? (
        <EmailDeliverabilityPage data={bundle.email} />
      ) : (
        <NotCaptured what="The email audit" />
      );
    case 'data-cloud':
      return bundle.datacloud ? (
        <DataCloudPage data={bundle.datacloud} />
      ) : (
        <NotCaptured what="The Data Cloud readiness audit" />
      );
  }
}

function subScoreColor(pct: number): string {
  return pct >= 70 ? '#3FA96B' : pct >= 45 ? '#C7851A' : '#D64545';
}

function Metric({ label, value }: { label: string; value: number | string | null | undefined }) {
  return (
    <div className="bg-surface-2 border border-border px-3 py-2.5">
      <div className="text-xl font-semibold text-crux-light tabular-nums leading-none">
        {value ?? '-'}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-1.5">{label}</div>
    </div>
  );
}

// The delete control needs the invite's own details, which the snapshot does
// not carry (it holds the org, not the client). The board is the one place
// they live, and it is one screen long.
type LeadHeader = {
  id: string;
  name: string;
  company: string;
  consentAt: number | null;
  baloClickedAt: number | null;
};

// Screen-shareable read-only view of a prospect's captured audit — the
// consultant's call-prep material. Renders purely from the stored snapshot;
// there is no Salesforce session behind it.
export function LeadSnapshotPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lead, setLead] = useState<LeadHeader | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('audit');
  // Held on the page, not inside the Categories tab, so the chosen category
  // survives a switch to another tab and back.
  const [category, setCategory] = useState<Category>('users');
  // Set by the data model view when the consultant clicks an automation count.
  const [openObject, setOpenObject] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    internalApi
      .get<Snapshot>(`/leads/${id}/snapshot`)
      .then((r) => setSnap(r.data))
      .catch((e) => {
        const m = (e as { response?: { status?: number; data?: { error?: string } } }).response;
        // A deleted record is not a prospect who has not connected yet: it is
        // gone, and only its approval record is left (TYR-219).
        if (m?.data?.error === 'record-deleted') {
          setError('This record was deleted. Only the approval record is kept.');
          return;
        }
        setError(
          m?.status === 404
            ? 'No snapshot yet. This prospect has not connected their org.'
            : m?.data?.error ?? (e as Error).message,
        );
      });
    internalApi
      .get<{ leads: LeadHeader[] }>('/leads')
      .then((r) => setLead(r.data.leads.find((l) => l.id === id) ?? null))
      .catch(() => setLead(null));
  }, [id]);

  const deleteRecord = async () => {
    if (!id) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const { data } = await internalApi.delete<{ outcome: DeleteOutcome }>(`/leads/${id}`);
      navigate('/internal/leads', { state: { deletedNotice: deleteRecordNotice(data.outcome) } });
    } catch (e) {
      const m = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setDeleteError(m ?? (e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  // Rendered under both the error state and the loaded scan, so a record can
  // still be deleted from a page whose snapshot failed to load.
  const deleteControl = lead ? (
    <>
      <button
        type="button"
        onClick={() => {
          setDeleteError(null);
          setConfirming(true);
        }}
        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border border-border text-neutral-400 hover:text-sunset hover:border-sunset/40"
      >
        <Trash2 size={13} />
        Delete record
      </button>
      <DeleteRecordDialog
        open={confirming}
        client={lead.company || lead.name}
        confirmWord={confirmWordFor(lead)}
        approved={lead.consentAt != null}
        busy={deleting}
        error={deleteError}
        onConfirm={() => void deleteRecord()}
        onCancel={() => setConfirming(false)}
      />
    </>
  ) : null;

  const back = (
    <Link
      to="/internal/leads"
      className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-crux-light mb-5"
    >
      <ArrowLeft size={15} />
      Client scans
    </Link>
  );

  if (error) {
    return (
      <div className="max-w-3xl">
        {back}
        <p className="text-sm text-neutral-400">{error}</p>
        <div className="mt-4">{deleteControl}</div>
      </div>
    );
  }

  if (!snap) {
    return (
      <div className="max-w-3xl">
        {back}
        <div className="flex items-center gap-2 text-neutral-500 text-sm">
          <Loader2 className="animate-spin" size={16} />
          Loading snapshot…
        </div>
      </div>
    );
  }

  const b = snap.bundle;
  const hs: HealthScore | undefined = b.healthscore;
  const o = b.overview?.totals;
  const findings = [...b.findings].sort((a, c) => SEV_RANK[a.severity] - SEV_RANK[c.severity]);

  return (
    // The two v2.4 tables are wide, so only the audit tab keeps the reading width.
    <div className={tab === 'audit' ? 'max-w-5xl' : ''}>
      {back}

      <div className="flex items-baseline gap-3 flex-wrap">
        <h1 className="text-3xl font-bold tracking-[-0.02em] text-crux-light">{snap.orgName}</h1>
        {snap.orgType === 'sandbox' ? (
          <span className="text-[11px] text-sunrise bg-sunrise/10 border border-sunrise/30 px-2 py-0.5">
            sandbox
          </span>
        ) : null}
        <div className="ml-auto self-center">{deleteControl}</div>
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Captured {new Date(snap.capturedAt).toLocaleString()} · aggregate counts only, no PII
        {lead?.baloClickedAt
          ? ` · Booking link clicked ${new Date(lead.baloClickedAt).toLocaleDateString()}`
          : ''}
      </p>

      <div className="flex border-b border-border-strong mt-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`text-sm font-semibold px-4 py-2.5 border border-b-0 -mb-px ${
              tab === t.key
                ? 'bg-surface-2 border-border-strong text-crux-light'
                : 'border-transparent text-neutral-500 hover:text-crux-light'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Automation by object, from the stored snapshot. Same component and
          same pure grouping the client Hub renders from a live session. */}
      {tab === 'automation' || tab === 'data-model' ? (
        <div className="mt-6">
          <AutomationTop10Card
            byObject={b.flows?.byObject}
            objects={b.objects}
            sharingRuleCounts={b.dataModelDetail?.sharingRuleCounts}
            // Approvals are read since v2.6 (automation detail); null = the read failed.
            approvalsCaptured={b.automationDetail != null}
            onOpen={(apiName) => {
              setOpenObject(apiName);
              setTab('automation');
            }}
          />
        </div>
      ) : null}

      {tab === 'automation' ? (
        <div>
          {b.flows?.byObject ? (
            <AutomationByObjectTab data={b.flows.byObject} openObject={openObject} scanObjects={b.objects ?? null} />
          ) : (
            <NotCaptured what="Automation by object" />
          )}
        </div>
      ) : null}

      {/* Data model view, cross-linked back to the automation tab. */}
      {tab === 'data-model' ? (
        <div>
          {b.objects ? (
            <ObjectsPage
              data={b.objects}
              flowsData={b.flows}
              detail={b.dataModelDetail ?? null}
              showIntro={false}
              onOpenAutomation={(apiName) => {
                setOpenObject(apiName);
                setTab('automation');
              }}
            />
          ) : (
            <NotCaptured what="The data model view" />
          )}
        </div>
      ) : null}

      {/* Every stored audit category, sub-menu on the left. */}
      {tab === 'categories' ? (
        <div className="mt-6 flex gap-6 items-start">
          <nav className="w-44 shrink-0 space-y-0.5">
            {CATEGORY_MENU.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                className={`w-full text-left text-sm px-3 py-1.5 border-l-2 ${
                  category === c.key
                    ? 'border-crux-light bg-surface-2 text-crux-light font-medium'
                    : 'border-transparent text-neutral-500 hover:text-crux-light'
                }`}
              >
                {c.label}
              </button>
            ))}
          </nav>
          <div className="flex-1 min-w-0">
            <CategoryView category={category} bundle={b} />
          </div>
        </div>
      ) : null}

      {/* Saved AI reports plus the PDF and DOCX of this stored scan. */}
      {tab === 'reports' ? (
        <div className="mt-6">
          <ScanReportsTab leadId={snap.leadId} />
        </div>
      ) : null}

      {tab === 'audit' ? (
        <>
      {/* Health score + Section 11 sub-scores */}
      <div className="mt-6 grid md:grid-cols-2 gap-8 items-center bg-surface-2 border border-border p-8">
        <div className="flex justify-center">
          {hs ? (
            <HealthGauge score={hs} />
          ) : (
            <p className="text-neutral-500 text-sm">Health score unavailable for this org.</p>
          )}
        </div>
        {hs ? (
          <div className="space-y-3">
            {CATEGORIES.map((c) => {
              const val = hs.breakdown[c.key];
              const pct = Math.round((val / c.max) * 100);
              return (
                <div key={c.key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-neutral-300">{c.label}</span>
                    <span className="tabular-nums text-neutral-400">
                      {val}/{c.max}
                    </span>
                  </div>
                  <div className="h-1.5 bg-surface-3">
                    <div
                      className="h-full"
                      style={{ width: `${pct}%`, background: subScoreColor(pct) }}
                    />
                  </div>
                </div>
              );
            })}
            {hs.foundations.eligible && hs.foundations.score !== null ? (
              <p className="text-[11px] text-neutral-500 pt-1">
                Foundations AI utilisation: {hs.foundations.score}/15
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Headline org metrics */}
      <h2 className="text-lg font-semibold text-crux-light mt-8 mb-3">Org at a glance</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <Metric label="Objects" value={o?.objects} />
        <Metric label="Custom objects" value={o?.customObjects} />
        <Metric label="Custom fields" value={o?.customFields} />
        <Metric label="Flows (active)" value={o?.activeFlows} />
        <Metric label="Workflow rules" value={o?.workflowRules} />
        <Metric label="Process builders" value={b.flows?.totals.processBuilders} />
        <Metric label="Apex classes" value={o?.apexClasses} />
        <Metric label="Apex triggers" value={o?.apexTriggers} />
        <Metric label="Users (active)" value={o?.activeUsers} />
        <Metric label="Inactive licensed" value={b.users?.totals.inactiveLicensed} />
        <Metric label="No login 90d" value={b.users?.totals.noLoginIn90Days} />
        <Metric label="System admins" value={b.permissions?.totals.systemAdminCount} />
      </div>

      {/* All findings */}
      <h2 className="text-lg font-semibold text-crux-light mt-8 mb-3">
        Findings <span className="text-neutral-500 font-normal">({findings.length})</span>
      </h2>
      {findings.length ? (
        <div className="space-y-2">
          {findings.map((f) => (
            <div key={f.id} className="flex items-start gap-3 bg-surface-2 border border-border p-4">
              <span
                className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border ${SEV_STYLE[f.severity]}`}
              >
                {f.severity}
              </span>
              <div>
                <div className="text-sm text-crux-light font-medium">{f.title}</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {f.category}
                  {f.context ? ` · ${f.context}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-500">No findings surfaced.</p>
      )}
        </>
      ) : null}
    </div>
  );
}
